import { quoteTransfer } from '@vemedia/transfer-quote'
import { computed, reactive, ref } from 'vue'
import { bound, resolveSeasonalRow, sumBreakdown } from '~~/shared/quote'

// Quote state + derived outputs for the tour price calculator. All quote rules live in
// `shared/quote.js`; this composable only wires tour-specific fields (per-category guest
// bands, group discount, flat per-region transfers, extras) to them.
//
// `config` is the static Storyblok tour content slice:
//   pricing                 pricing bloks: category (adult|child|infant), min_guests,
//                           max_guests, season_start/end, price
//   extras                  tour-extra bloks: label, price, unit (per_booking|per_person),
//                           default_selected — deliberately the same shape as package addons
//   transfers               transfer-cost bloks: hotel_region, cost — flat per-booking price
//   hasGroupDiscount,
//   groupDiscountMinSize,
//   groupDiscountPercentage group discount applies to the guest subtotal only
//   name, pageUrl           used in the WhatsApp summary + modal payload
//
// Returns reactive inputs (mutate directly from the calculator's controls) and computed
// outputs; `bookingDetails` keeps the snake_case modal shape so one BookingModal serves
// every content type.
//
// ref/computed/reactive are imported explicitly (unlike the rest of the app) so the
// parity matrix in `test/tour-quote.test.js` can run the composable under plain vitest.

export const CATEGORIES = [
    { value: 'adult', label: 'Adults' },
    { value: 'child', label: 'Children' },
    { value: 'infant', label: 'Infants' },
]

export const REGIONS = {
    'north-coast': 'North Coast (Nungwi, Kendwa)',
    'stone-town': 'Stone Town (Zanzibar City)',
    'south-coast': 'South Coast (Kizimkazi, Makunduchi)',
    'west-coast': 'West Coast (Bububu, Mangapwani, Fumba)',
    'east-coast': 'East Coast (Matemwe, Kiwengwa, Paje, Jambiani)',
    'moshi-town': 'Moshi Town (Hotel pickup)',
    'jro-airport': 'Kilimanjaro International Airport (JIA / JRO)',
    'arusha-town': 'Arusha Town',
}

export function regionLabel(region) {
    return REGIONS[region] || region
}

export function useTourQuote(config) {
    const pricing = config.pricing || []
    const extras = config.extras || []
    const transfers = config.transfers || []
    const dashboardTransfer = config.dashboardTransfer || null

    // Pricing is static per page, so the category list needs no reactivity.
    const availableCategories = CATEGORIES.filter(cat => pricing.some(row => row.category === cat.value))
    const hasSeasonalPricing = pricing.some(row => row.season_start || row.season_end)
        || !!dashboardTransfer?.hasSeasonalPricing

    // --- Inputs (calculator controls bind straight onto these) ---
    const guests = reactive(
        Object.fromEntries(
            CATEGORIES.map(cat => [cat.value, cat.value === 'adult' && availableCategories.some(c => c.value === 'adult') ? 2 : 0]),
        ),
    )
    const travelDate = ref('')
    const hotelRegion = ref('')
    const selectedExtras = reactive(
        Object.fromEntries(extras.map(ex => [ex._uid, !!ex.default_selected])),
    )

    // KNumberField reports `undefined` when its input is cleared — normalize to a
    // non-negative integer so the totals stay well-defined.
    function setGuests(category, value) {
        guests[category] = Math.max(0, Math.round(Number(value) || 0))
    }

    const totalGuests = computed(() =>
        availableCategories.reduce((sum, cat) => sum + guests[cat.value], 0),
    )

    // The applicable per-person price for a category given group size + date: filter to
    // the category's rows whose guest-count band fits, then the shared season resolution
    // picks between seasonal rows and the all-year fallback. Rows without a band or
    // season act as unconditional fallbacks, so a simple single-price tour "just works"
    // with one row.
    function priceFor(category) {
        const rows = pricing.filter(row =>
            row.category === category
            && totalGuests.value >= bound(row.min_guests, -Infinity)
            && totalGuests.value <= bound(row.max_guests, Infinity))
        const row = resolveSeasonalRow(rows, travelDate.value)
        return row ? Number(row.price) : null
    }

    const missingPriceNote = computed(() =>
        availableCategories.some(cat => guests[cat.value] > 0 && priceFor(cat.value) === null),
    )

    const groupDiscountPct = computed(() => {
        if (!config.hasGroupDiscount)
            return null
        const min = Number(config.groupDiscountMinSize) || 0
        const pct = Number(config.groupDiscountPercentage) || 0
        if (!pct || totalGuests.value < min)
            return null
        return pct
    })

    const dashboardTransferQuote = computed(() => {
        if (!dashboardTransfer || !hotelRegion.value)
            return null
        return quoteTransfer(dashboardTransfer.catalog, {
            fromPlaceId: Number(hotelRegion.value),
            toPlaceId: dashboardTransfer.activityId,
            tripType: 'round_trip',
            date: travelDate.value,
            passengers: totalGuests.value,
        })
    })

    const selectedPickup = computed(() =>
        dashboardTransfer?.pickupPlaces.find(place => place.id === Number(hotelRegion.value)) || null,
    )

    const breakdown = computed(() => {
        const lines = []

        let guestSubtotal = 0
        availableCategories.forEach((cat) => {
            const count = guests[cat.value]
            if (count <= 0)
                return
            const unit = priceFor(cat.value)
            if (unit === null)
                return
            guestSubtotal += count * unit
            lines.push({ label: `${cat.label} ${count} x $${unit}`, amount: count * unit })
        })

        if (groupDiscountPct.value) {
            const amount = -Math.round(guestSubtotal * groupDiscountPct.value / 100)
            if (amount)
                lines.push({ label: `Group discount (${groupDiscountPct.value}%)`, amount })
        }

        extras.forEach((ex) => {
            if (!selectedExtras[ex._uid])
                return
            const price = Number(ex.price)
            const amount = ex.unit === 'per_person' ? price * totalGuests.value : price
            lines.push({ label: `${ex.label}`, amount })
        })

        if (dashboardTransferQuote.value && selectedPickup.value) {
            const quote = dashboardTransferQuote.value
            const vehicles = quote.quantity > 1 ? ` (${quote.quantity} vehicles)` : ''
            const display = quote.kind === 'included'
                ? 'Included with the tour'
                : quote.kind === 'on_request' ? 'Price confirmed with your booking' : `$${quote.amount}`
            lines.push({
                label: `Transfer — ${selectedPickup.value.name}${vehicles}`,
                amount: quote.amount ?? 0,
                display,
            })
        } else if (!dashboardTransfer && hotelRegion.value) {
            const transfer = transfers.find(t => t.hotel_region === hotelRegion.value)
            if (transfer) {
                lines.push({ label: `Transfer — ${regionLabel(hotelRegion.value)}`, amount: Number(transfer.cost) })
            }
        }

        return lines
    })

    const total = computed(() => sumBreakdown(breakdown.value))

    // "From" price: cheapest adult price across every band/season, falling back to the
    // cheapest of any row. Feeds the meta strip, the JSON-LD Offer and `tour_viewed`.
    const fromPrice = computed(() => {
        const adultPrices = pricing.filter(row => row.category === 'adult').map(row => Number(row.price))
        const prices = adultPrices.length ? adultPrices : pricing.map(row => Number(row.price))
        return prices.length ? Math.min(...prices) : null
    })

    // --- Booking flow (WhatsApp summary + modal payload) ---
    const guestsText = computed(() =>
        availableCategories
            .filter(cat => guests[cat.value] > 0)
            .map(cat => `${guests[cat.value]} ${cat.label.toLowerCase()}`)
            .join(', ') || 'No guests selected',
    )

    const extrasText = computed(() =>
        extras.filter(ex => selectedExtras[ex._uid]).map(ex => ex.label).join(', '),
    )

    const transferText = computed(() => {
        if (!hotelRegion.value)
            return ''
        if (dashboardTransfer) {
            const pickup = selectedPickup.value
            const quote = dashboardTransferQuote.value
            if (!pickup || !quote)
                return ''
            const vehicles = quote.quantity > 1 ? `, ${quote.quantity} vehicles` : ''
            if (quote.kind === 'included')
                return `${pickup.name} (included with the tour${vehicles})`
            if (quote.kind === 'on_request')
                return `${pickup.name} (price confirmed with your booking${vehicles})`
            return `${pickup.name} (+$${quote.amount}${vehicles})`
        }
        const transfer = transfers.find(t => t.hotel_region === hotelRegion.value)
        return transfer ? `${regionLabel(hotelRegion.value)} (+$${Number(transfer.cost)})` : ''
    })

    const summaryText = computed(() => {
        const lines = [
            `Hi! I'd like to book: ${config.name}`,
            `Guests: ${guestsText.value}`,
        ]
        if (travelDate.value)
            lines.push(`Travel date: ${travelDate.value}`)
        if (transferText.value)
            lines.push(`Hotel transfer: ${transferText.value}`)
        if (extrasText.value)
            lines.push(`Extras: ${extrasText.value}`)
        if (groupDiscountPct.value)
            lines.push(`Group discount: -${groupDiscountPct.value}%`)
        lines.push(`Estimated total: $${total.value}`)
        lines.push(config.pageUrl)
        return lines.join('\n')
    })

    const bookingDetails = computed(() => ({
        ...(config.itemUuid && { item_uuid: config.itemUuid }),
        tour: config.name,
        guests: totalGuests.value,
        guest_breakdown: { ...guests },
        guests_text: guestsText.value,
        travel_date: travelDate.value,
        transfer_text: transferText.value,
        extras_text: extrasText.value,
        total: total.value,
        page_url: config.pageUrl,
    }))

    return {
        // inputs
        guests,
        travelDate,
        hotelRegion,
        selectedExtras,
        setGuests,
        // quote outputs
        availableCategories,
        hasSeasonalPricing,
        totalGuests,
        missingPriceNote,
        groupDiscountPct,
        dashboardTransferQuote,
        breakdown,
        total,
        fromPrice,
        // booking flow
        guestsText,
        extrasText,
        transferText,
        summaryText,
        bookingDetails,
    }
}
